/**
 * ============================================================
 * KEJJA OS — src/pages/community/CommunityListPage.tsx
 * Landing: my communities + create + join-by-code.
 * ============================================================
 */

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, KeyRound, Users2, Landmark, MapPin, Heart, Circle,
  ChevronRight, X,
} from 'lucide-react';

import { useCommunityContext } from './CommunityLayout';
import { useMyCommunities, useCreateCommunity, useJoinByCode } from '@/hooks/useCommunity';
import type { CommunityType, CommunityWithRole } from '@/lib/communityApi';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { EmojiPicker } from '@/components/EmojiPicker';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const TYPE_DEFS: Array<{
  value: CommunityType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}> = [
  { value: 'chama', label: 'Chama', icon: Landmark, description: 'Savings group / merry-go-round' },
  { value: 'family_extended', label: 'Extended family', icon: Heart, description: 'Parents, siblings, relatives' },
  { value: 'neighborhood', label: 'Neighborhood', icon: MapPin, description: 'Local area info board' },
  { value: 'fellowship', label: 'Fellowship', icon: Users2, description: 'Church, faith, small group' },
  { value: 'general', label: 'General', icon: Circle, description: 'Anything else' },
];

export default function CommunityListPage() {
  const { homeId } = useCommunityContext();
  const { data, isLoading } = useMyCommunities(homeId);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);

  const communities = data?.communities ?? [];

  return (
    <div className="space-y-5 pb-12">
      {/* Action buttons */}
      <div className="flex gap-2">
        <Button
          size="sm"
          className="bg-rose-600 hover:bg-rose-700 text-white flex-1"
          onClick={() => setShowCreate(true)}
        >
          <Plus className="w-4 h-4 mr-1" /> New community
        </Button>
        <Button
          size="sm" variant="outline"
          onClick={() => setShowJoin(true)}
        >
          <KeyRound className="w-4 h-4 mr-1" /> Join by code
        </Button>
      </div>

      {/* Communities grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Skeleton className="h-24" /><Skeleton className="h-24" />
        </div>
      ) : communities.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <Users2 className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              You're not in any communities yet. Create a new one or join with a code.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {communities.map((c) => (
            <CommunityCard key={c.id} c={c} />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateCommunityModal homeId={homeId} onClose={() => setShowCreate(false)} />
      )}
      {showJoin && (
        <JoinByCodeModal homeId={homeId} onClose={() => setShowJoin(false)} />
      )}
    </div>
  );
}

// ============================================================
// CommunityCard
// ============================================================

function CommunityCard({ c }: { c: CommunityWithRole }) {
  const typeDef = TYPE_DEFS.find((t) => t.value === c.community_type) ?? TYPE_DEFS[TYPE_DEFS.length - 1]!;
  const Icon = typeDef.icon;

  return (
    <Link to={`/community/${c.id}`} className="block">
      <Card className="hover:ring-1 hover:ring-rose-500/40 transition-all cursor-pointer">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 flex items-center justify-center flex-shrink-0 text-lg">
            {c.avatar_emoji ?? <Icon className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="font-semibold text-sm truncate flex-1">{c.name}</div>
              {c.role === 'admin' && (
                <Badge variant="outline" className="text-[9px] h-4 px-1 border-rose-300 text-rose-700 dark:text-rose-300">
                  admin
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-0.5">
              <Icon className="w-2.5 h-2.5" />
              <span>{typeDef.label}</span>
              {c.location_name && (
                <><span>·</span><span className="truncate">{c.location_name}</span></>
              )}
            </div>
            {c.description && (
              <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{c.description}</p>
            )}
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />
        </CardContent>
      </Card>
    </Link>
  );
}

// ============================================================
// Create community modal
// ============================================================

function CreateCommunityModal({ homeId, onClose }: { homeId: string; onClose: () => void }) {
  const createMut = useCreateCommunity(homeId);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [communityType, setCommunityType] = useState<CommunityType>('chama');
  const [locationName, setLocationName] = useState('');
  const [avatarEmoji, setAvatarEmoji] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) { setErr('Name is required.'); return; }

    createMut.mutate({
      name: name.trim(),
      description: description.trim() || null,
      communityType,
      locationName: communityType === 'neighborhood' ? (locationName.trim() || null) : null,
      avatarEmoji: avatarEmoji.trim() || null,
    }, {
      onSuccess: () => onClose(),
      onError: (e) => setErr(e.message ?? 'Could not create'),
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card className="w-full max-w-lg my-8">
        <CardContent className="p-5">
          <form onSubmit={submit} className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">New community</h2>
              <Button type="button" size="sm" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
            </div>

            <div>
              <Label className="text-xs">Type</Label>
              <div className="grid grid-cols-2 gap-1.5 mt-1">
                {TYPE_DEFS.map((t) => {
                  const Icon = t.icon;
                  const on = communityType === t.value;
                  return (
                    <button
                      key={t.value} type="button"
                      onClick={() => setCommunityType(t.value)}
                      className={cn(
                        'flex items-center gap-2 px-2.5 py-2 rounded-lg border text-left transition-colors',
                        on
                          ? 'border-rose-500/60 bg-rose-500/10 text-rose-700 dark:text-rose-300'
                          : 'border-border text-muted-foreground',
                      )}
                    >
                      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-xs font-semibold">{t.label}</div>
                        <div className="text-[9px] opacity-70 truncate">{t.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-[1fr_auto] gap-2">
              <div>
                <Label className="text-xs">Name</Label>
                <Input
                  autoFocus value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Okoth Family Chama"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Emoji</Label>
                <div className="mt-1 w-16">
                  <EmojiPicker value={avatarEmoji} onChange={setAvatarEmoji} />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs">Description (optional)</Label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What's this community about?"
                className="w-full mt-1 min-h-[60px] rounded-md border border-border bg-background p-2 text-xs"
              />
            </div>

            {communityType === 'neighborhood' && (
              <div>
                <Label className="text-xs">Location</Label>
                <Input
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                  placeholder="e.g. Kileleshwa, Nairobi"
                  className="mt-1"
                />
              </div>
            )}

            {err && <div className="text-xs text-rose-600 font-medium">{err}</div>}

            <div className="flex gap-2 pt-2 border-t border-border">
              <Button
                type="submit"
                className="bg-rose-600 hover:bg-rose-700 text-white flex-1"
                disabled={createMut.isPending}
              >
                {createMut.isPending ? 'Creating…' : 'Create community'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================
// Join by code modal
// ============================================================

function JoinByCodeModal({ homeId, onClose }: { homeId: string; onClose: () => void }) {
  const joinMut = useJoinByCode(homeId);
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!code.trim()) { setErr('Enter a code.'); return; }

    joinMut.mutate(code.trim().toUpperCase(), {
      onSuccess: () => onClose(),
      onError: (e) => setErr(e.message ?? 'Invalid or full code.'),
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card className="w-full max-w-sm my-16">
        <CardContent className="p-5">
          <form onSubmit={submit} className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">Join by code</h2>
              <Button type="button" size="sm" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
            </div>

            <div>
              <Label className="text-xs">Join code</Label>
              <Input
                autoFocus value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="ABCD1234"
                className="mt-1 text-center font-mono tracking-wider uppercase"
                maxLength={12}
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Ask the community admin for their code.
              </p>
            </div>

            {err && <div className="text-xs text-rose-600 font-medium">{err}</div>}

            <Button
              type="submit"
              className="bg-rose-600 hover:bg-rose-700 text-white w-full"
              disabled={joinMut.isPending}
            >
              {joinMut.isPending ? 'Joining…' : 'Join'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
